// HUD entry point. Builds every DOM element inside #ui and wires it to the
// GameState blackboard: top bar, build bar, chat, toasts, join modal, minimap
// frame, and the bigger panels from panels.ts.

import { hexToPixel } from '../../shared/hex'
import {
  BUILDINGS,
  CHAT_MAX,
  DAY_TICKS,
  GROWTH_FOOD_MIN,
  GROWTH_TICKS,
  NAME_MAX,
  TICK_MS,
  isBoxOrder,
  treeWood,
} from '../../shared/constants'
import { RESOURCES, Terrain } from '../../shared/types'
import { TERRAIN, isPassable } from '../../shared/terrain'
import { randomRealmName } from '../../shared/names'
import { paintLandingMap } from './landingMap'
import { initSound, setMuted, soundsMuted } from '../sound'
import { initTutorial } from './tutorial'
import { initAdventures } from './adventures'
import { initStorageHover } from './storageHover'
import { createBuildingPreviews } from './modelPreview'
import { initJournal } from './journal'
import { initMobileUI, MOBILE_HUD_QUERY } from './mobile'
import { initAssignModal } from './crewModal'
import type { BuildingType, GameEvent, Resource, Stockpile } from '../../shared/types'
import type { ClientMsg } from '../../shared/protocol'
import type { GameState } from '../state'
import { HEX_SIZE, predictLocal , use3dView } from '../state'
import { screenToWorld } from '../input'
import { elevAt, hexAtWorld, ownerAt, roadAt, terrainAt, treeAt } from '../render/terrain'
import {
  BUILD_ICON,
  RES_ICON,
  buildTooltip,
  canAfford,
  colorOf,
  el,
  fmtCost,
  initChronicle,
  initDefeatOverlay,
  initDiplomacy,
  initChangelog,
  initHelpOverlay,
  initRoster,
  initSelectionPanel,
  type UICtx,
} from './panels'

const DISCORD_SVG =
  '<svg class="discord-mark" viewBox="0 0 127.14 96.36" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83A97.68 97.68 0 0 0 49 6.83 72.37 72.37 0 0 0 45.64 0 105.89 105.89 0 0 0 19.39 8.09C2.79 32.65-1.71 56.6.54 80.21h0A105.73 105.73 0 0 0 32.71 96.36 77.7 77.7 0 0 0 39.6 85.25a68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1A105.25 105.25 0 0 0 126.6 80.22h0C129.24 52.84 122.09 29.11 107.7 8.07ZM42.45 65.69C36.18 65.69 31 60 31 53s5-12.74 11.43-12.74S54 46 53.89 53 48.84 65.69 42.45 65.69Zm42.24 0C78.41 65.69 73.25 60 73.25 53s5-12.74 11.44-12.74S96.23 46 96.12 53 91.08 65.69 84.69 65.69Z"/></svg>'

const BUILD_ORDER: BuildingType[] = [
  'townhall',
  'house',
  'farm',
  'chop',
  'quarry',
  'mine',
  'storehouse',
  'workbench',
  'barracks',
  'tower',
  'road',
]

/** One-line gameplay note per terrain type, for the tile hover tooltip. */
const TERRAIN_HINT: Record<Terrain, string> = {
  [Terrain.DeepWater]: 'Open sea — impassable. It bounds your coastline.',
  [Terrain.Water]: 'Water — impassable to land units. Cross around it.',
  [Terrain.Sand]: 'Coastal sand — buildable, but folk cross it slowly.',
  [Terrain.Grass]: 'Grassland — your workhorse tile: houses, farms and quarries all sit here.',
  [Terrain.Fertile]: 'Fertile plain — farms here yield +50% food. Prime farmland.',
  [Terrain.Forest]: 'Forest — impassable, but chop it down (Gathering ▸ Chop Trees) for wood.',
  [Terrain.Hills]: 'Hills — build a Quarry here for stone.',
  [Terrain.Mountain]: 'Mountains — high, steep and slow to cross, but passable. A Mine beside them digs iron & gold.',
  [Terrain.Swamp]: 'Swamp — passable but very slow going, and nothing builds here.',
  [Terrain.Snow]: 'Snowfield — buildable and passable, if a little slow.',
}

export function initUI(
  st: GameState,
  send: { current: ((m: ClientMsg) => void) | null },
): { showLogin: () => void; setAccount: (provider?: string) => void } {
  const root = document.getElementById('ui') as HTMLElement
  const tx = (m: ClientMsg): void => {
    predictLocal(st, m) // instant local echo; the server confirms right after
    send.current?.(m)
  }
  const jumpTo = (q: number, r: number): void => {
    const p = hexToPixel(q, r, HEX_SIZE)
    st.cam.x = p.x
    st.cam.y = p.y
  }
  const ctx: UICtx = { st, tx, root, jumpTo }
  initStorageHover(ctx)
  initSound(st)
  initTutorial(ctx)
  initJournal(ctx)
  initAdventures(ctx)
  root.appendChild(el('div', 'alpha-tag', '⚠ early alpha · world may be wiped'))

  // ---- Lag chip: name the network so a stall doesn't read as a broken game.
  // Shown when no tick has landed for >1.5s while connected; the dead-reckoned
  // serfs keep walking underneath it.
  const lagChip = el('div', 'lag-chip hidden', '⏳ catching up…')
  root.appendChild(lagChip)
  setInterval(() => {
    const stalled = st.connected && st.net.lastTickAt > 0 && Date.now() - st.net.lastTickAt > 1500
    lagChip.classList.toggle('hidden', !stalled)
  }, 500)

  // ---- Top bar -------------------------------------------------------------

  const topbar = el('div', 'panel topbar')
  root.appendChild(topbar)

  const crest = el('div', 'tb-crest')
  crest.appendChild(el('span', 'crest-mark', 'S'))
  const crestText = el('div', 'crest-text', 'SERF LAND')
  crestText.appendChild(el('span', 'crest-sub', 'A kingdom in the making'))
  crest.appendChild(crestText)
  topbar.appendChild(crest)

  const resWrap = el('div', 'tb-res')
  topbar.appendChild(resWrap)
  const resAmount = {} as Record<Resource, HTMLElement>
  const resDelta = {} as Record<Resource, HTMLElement>
  const resChip = {} as Record<Resource, HTMLElement>

  // Detailed production/consumption tooltip, shown on hovering a resource chip.
  const resTip = el('div', 'restip hidden')
  root.appendChild(resTip)
  let hoverRes: Resource | null = null
  let hoverChip: HTMLElement | null = null

  for (const res of RESOURCES) {
    const chip = el('div', 'chip res')
    chip.dataset.resource = res
    chip.tabIndex = 0
    chip.setAttribute('aria-label', res + ' stores and production')
    chip.appendChild(el('span', 'chip-icon', RES_ICON[res]))
    chip.appendChild(el('span', 'chip-name', res))
    const amt = el('span', 'chip-amt', '—')
    const dlt = el('span', 'chip-delta', '')
    chip.appendChild(amt)
    chip.appendChild(dlt)
    resWrap.appendChild(chip)
    resAmount[res] = amt
    resDelta[res] = dlt
    resChip[res] = chip
    chip.addEventListener('mouseenter', () => {
      hoverRes = res
      hoverChip = chip
      resTip.classList.remove('hidden')
      renderResTip()
    })
    chip.addEventListener('mouseleave', () => {
      if (hoverRes !== res) return
      hoverRes = null
      hoverChip = null
      resTip.classList.add('hidden')
    })
    chip.addEventListener('focus', () => {
      hoverRes = res; hoverChip = chip
      resTip.classList.remove('hidden')
      renderResTip()
    })
    chip.addEventListener('blur', () => {
      hoverRes = null; hoverChip = null
      resTip.classList.add('hidden')
    })
  }

  function fmtRate(n: number): string {
    const a = Math.abs(n)
    return (n > 0 ? '+' : n < 0 ? '−' : '') + a.toFixed(a >= 10 ? 0 : 1) + '/min'
  }

  function renderResTip(): void {
    if (!hoverRes || !hoverChip) return
    const res = hoverRes
    const self = st.self
    resTip.innerHTML = ''

    const head = el('div', 'rt-head')
    head.appendChild(el('span', 'rt-ico', RES_ICON[res]))
    head.appendChild(el('span', 'rt-name', res.charAt(0).toUpperCase() + res.slice(1)))
    resTip.appendChild(head)
    if (self) {
      let ttl = 0
      for (const r2 of RESOURCES) ttl += self.stockpile[r2] ?? 0
      resTip.appendChild(
        el('div', 'rt-sub', Math.floor(self.stockpile[res] ?? 0) + ' stored · realm ' + Math.floor(ttl) + '/' + self.capacity + ' items'),
      )
    }

    const flow = self?.flows?.[res]
    if (res === 'food' && self?.food) {
      const food = self.food
      resTip.append(el('div', 'rt-body', `Regular production supports ${food.supports} people; your village has ${self.pop}.`),
        el('div', 'rt-sub', `Producing ${food.production.toFixed(1)}/min · eating ${food.consumption.toFixed(1)}/min`),
        el('div', 'rt-empty', 'Starter supplies and milestone gifts fill the shelf once. They do not increase ongoing production.'))
    }
    if (!flow || flow.lines.length === 0) {
      resTip.appendChild(el('div', 'rt-empty', 'Nothing is producing or consuming this yet.'))
    } else {
      const list = el('div', 'rt-lines')
      const lines: typeof flow.lines = res === 'food' && self?.food
        ? [{ label: 'Working farms and piers', rate: self.food.production }, { label: 'Villagers', rate: -self.food.consumption }]
        : flow.lines
      for (const ln of lines) {
        const row = el('div', 'rt-line')
        row.appendChild(el('span', 'rt-label', ln.label))
        const cls = ln.idle ? 'idle' : ln.rate > 0 ? 'up' : ln.rate < 0 ? 'down' : ''
        row.appendChild(el('span', 'rt-rate ' + cls, ln.idle ? 'idle' : fmtRate(ln.rate)))
        list.appendChild(row)
      }
      resTip.appendChild(list)
      const net = el('div', 'rt-net')
      net.appendChild(el('span', 'rt-label', 'Net'))
      const rate = res === 'food' && self?.food ? self.food.net : flow.net
      const ncls = rate > 0.05 ? 'up' : rate < -0.05 ? 'down' : ''
      net.appendChild(el('span', 'rt-rate ' + ncls, fmtRate(rate)))
      resTip.appendChild(net)
    }

    const stores = self?.stores?.[res]
    if (stores && stores.length) {
      resTip.appendChild(el('div', 'rt-section', 'Stored in'))
      const slist = el('div', 'rt-lines')
      for (const s of stores) {
        const row = el('div', 'rt-line')
        row.appendChild(el('span', 'rt-label', s.label))
        row.appendChild(el('span', 'rt-store', `${s.qty} / ${s.cap}`))
        slist.appendChild(row)
      }
      resTip.appendChild(slist)
    }

    const r = hoverChip.getBoundingClientRect()
    resTip.style.left = Math.round(r.left) + 'px'
    resTip.style.top = Math.round(r.bottom + 8) + 'px'
  }
  // Idle-hands chip: shows how many serfs are doing nothing; click selects them.
  const idleChip = el('div', 'chip idle idle-chip hidden')
  idleChip.tabIndex = 0
  idleChip.setAttribute('role', 'button')
  idleChip.setAttribute('aria-label', 'Select idle workers')
  idleChip.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault(); e.stopPropagation(); idleChip.click()
  })
  idleChip.appendChild(el('span', 'chip-icon', '💤'))
  const idleAmt = el('span', 'chip-amt', '0')
  idleChip.appendChild(idleAmt)
  idleChip.title = 'Idle serfs — click to select them all'
  idleChip.style.cursor = 'pointer'
  idleChip.onclick = () => {
    const ids: number[] = []
    for (const u of st.units.values()) {
      if (u.owner === st.me && u.type === 'citizen' && !u.hauler && u.state === 'idle' && !u.scav && u.supply === undefined) {
        ids.push(u.id)
      }
    }
    if (ids.length) st.selection = { units: ids, building: null }
  }
  resWrap.appendChild(idleChip)

  // Starter guidance: one contextual next-step hint for young villages.
  const hintBar = el('div', 'panel hintbar hidden')
  const hintTxt = el('span', 'hint-txt', '')
  hintBar.appendChild(hintTxt)
  const hintClose = el('button', 'btn btn-ghost btn-sm', '✕')
  hintClose.title = 'Hide hints'
  hintClose.setAttribute('aria-label', 'Hide hints')
  hintClose.onclick = () => {
    hintsOff = true
    try { localStorage.setItem('hexHintsOff', '1') } catch { /* private mode */ }
    hintBar.classList.add('hidden')
  }
  hintBar.appendChild(hintClose)
  root.appendChild(hintBar)
  let hintsOff = false
  try { hintsOff = localStorage.getItem('hexHintsOff') === '1' } catch { /* private mode */ }

  function refreshHint(): void {
    if (hintsOff || !st.me || !st.self) {
      hintBar.classList.add('hidden')
      return
    }
    const have = new Set<string>()
    let sites = 0
    for (const b of st.buildings.values()) {
      if (b.owner !== st.me) continue
      if (b.done) have.add(b.type)
      else sites++
    }
    const wood = st.self.stockpile.wood ?? 0
    let hint = ''
    const food = st.self.food
    if (food && food.net < -0.1 && sites === 0) hint = `🌾 Production feeds ${food.supports} of ${st.self.pop} people — add a Farm or a shoreline Fishing Pier.`
    else if (!have.has('farm') && !have.has('pier') && sites === 0) hint = '🌾 Your folk must eat — build a Farm or a shoreline Fishing Pier.'
    else if (have.has('farm') && wood < 30 && sites === 0) hint = '🪓 Wood drives everything: give a serf an axe and chop trees (Gathering ▸ Chop Trees).'
    else if (st.self.pop >= st.self.popCap && !have.has('house')) hint = '🏠 Housing is full — build Houses so the village can grow.'
    else if (have.has('farm') && !have.has('tower') && st.self.pop >= 8) hint = '🗼 Raiders prowl the wilds — a Watchtower guards your borders.'
    if (hint) {
      hintTxt.textContent = hint
      hintBar.classList.remove('hidden')
    } else {
      hintBar.classList.add('hidden')
    }
  }

  const roster = initRoster(ctx)
  // The crew editor shows a pending state until the authoritative crew arrives.
  // Skip the generic optimistic echo so a rejected move cannot look successful.
  const assign = initAssignModal({ ...ctx, tx: m => send.current?.(m) }, type => previews.get(type))
  const changelog = initChangelog(ctx)
  const popChip = el('div', 'chip pop population-chip')
  popChip.tabIndex = 0
  popChip.setAttribute('role', 'button')
  popChip.setAttribute('aria-label', 'Open village roster')
  popChip.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault(); e.stopPropagation(); popChip.click()
  })
  popChip.style.cursor = 'pointer'
  popChip.title = 'Your people — click for the full roster (R)'
  popChip.onclick = () => roster.toggle()
  popChip.appendChild(el('span', 'chip-icon', '👥'))
  const popAmt = el('span', 'chip-amt', '—')
  popChip.appendChild(popAmt)
  resWrap.appendChild(popChip)

  // Population tooltip: explains how folk grow, with live status.
  const popTip = el('div', 'restip hidden')
  root.appendChild(popTip)
  let popHovered = false

  function renderPopTip(): void {
    if (!popHovered) return
    const self = st.self
    popTip.innerHTML = ''
    const head = el('div', 'rt-head')
    head.appendChild(el('span', 'rt-ico', '👥'))
    head.appendChild(el('span', 'rt-name', 'Population'))
    popTip.appendChild(head)
    if (self) popTip.appendChild(el('div', 'rt-sub', self.pop + ' folk · housing for ' + self.popCap))
    popTip.appendChild(
      el('div', 'rt-body', 'New folk are born in your Town Hall — roughly one every ' +
        Math.round((GROWTH_TICKS * TICK_MS) / 1000) + 's per hall when there is spare housing and more than zero food in storage. Production rate does not limit arrivals. You can pause arrivals in the Village Council.'),
    )
    if (self) {
      const line = el('div', 'rt-lines')
      const row = el('div', 'rt-line')
      row.appendChild(el('span', 'rt-label', 'Status'))
      let txt: string
      let cls: string
      if (self.ruined) {
        txt = 'village in ruins'
        cls = 'down'
      } else if (self.pop >= self.popCap) {
        txt = 'housing full'
        cls = 'idle'
      } else if (self.food && !self.food.canGrow) {
        txt = self.food.growth === 'paused' ? 'arrivals paused' : 'waiting for food'
        cls = 'idle'
      } else if ((self.stockpile.food ?? 0) <= GROWTH_FOOD_MIN) {
        txt = 'waiting for food'
        cls = 'idle'
      } else {
        txt = 'growing'
        cls = 'up'
      }
      row.appendChild(el('span', 'rt-rate ' + cls, txt))
      line.appendChild(row)
      popTip.appendChild(line)
      const tip = self.food?.reason ?? (
        self.pop >= self.popCap
          ? 'Build a House (+5) or upgrade your Town Hall for more housing.'
          : (self.stockpile.food ?? 0) <= GROWTH_FOOD_MIN
            ? 'Grow, catch or hunt for food. Arrivals resume once there is food in storage.'
            : 'New arrivals continue while there is food in storage and spare housing.')
      popTip.appendChild(el('div', 'rt-empty', tip))
    }
    const r = popChip.getBoundingClientRect()
    popTip.style.left = Math.round(Math.max(8, r.left - 40)) + 'px'
    popTip.style.top = Math.round(r.bottom + 8) + 'px'
  }
  popChip.addEventListener('mouseenter', () => {
    popHovered = true
    popTip.classList.remove('hidden')
    renderPopTip()
  })
  popChip.addEventListener('mouseleave', () => {
    popHovered = false
    popTip.classList.add('hidden')
  })

  // Tile info: terrain type + gameplay note, shown in a fixed panel that sits
  // directly above the unit-info (selection) panel in the bottom-left, tracking
  // whatever tile the cursor is over.
  const gameCanvas = document.getElementById('game') as HTMLCanvasElement | null
  const blStack = el('div', 'bl-stack')
  root.appendChild(blStack)
  const tilePanel = el('div', 'panel tile-panel hidden')
  blStack.appendChild(tilePanel)
  let lastTileKey = ''

  function fillTilePanel(q: number, r: number): void {
    const t = terrainAt(st, q, r)
    tilePanel.innerHTML = ''
    tilePanel.appendChild(el('div', 'tile-cap', 'Tile'))
    const head = el('div', 'rt-head')
    if (t === undefined) {
      head.appendChild(el('span', 'rt-name', 'Unexplored'))
      tilePanel.appendChild(head)
      tilePanel.appendChild(el('div', 'rt-sub', 'Move over the map to reveal the land.'))
      return
    }
    const def = TERRAIN[t]
    const sw = el('span', 'rt-swatch')
    sw.style.background = def.color
    head.appendChild(sw)
    head.appendChild(el('span', 'rt-name', def.name))
    tilePanel.appendChild(head)
    tilePanel.appendChild(
      el('div', 'rt-sub', (isPassable(t) ? 'Passable' : 'Impassable') + ' · ' + (def.buildable ? 'buildable' : 'not buildable')),
    )
    tilePanel.appendChild(el('div', 'rt-body', TERRAIN_HINT[t]))
    const extra = el('div', 'rt-lines')
    let any = false
    const tier = elevAt(st, q, r)
    if (tier !== undefined) {
      const row = el('div', 'rt-line')
      row.appendChild(el('span', 'rt-label', '⛰️ Height'))
      row.appendChild(el('span', 'rt-rate', `tier ${tier}`))
      extra.appendChild(row)
      any = true
    }
    if (treeAt(st, q, r)) {
      const row = el('div', 'rt-line')
      row.appendChild(el('span', 'rt-label', '🌲 Wooded'))
      row.appendChild(el('span', 'rt-rate up', `${treeWood(q, r)} wood`))
      extra.appendChild(row)
      any = true
    }
    if (roadAt(st, q, r)) {
      const row = el('div', 'rt-line')
      row.appendChild(el('span', 'rt-label', '🛤️ Road'))
      row.appendChild(el('span', 'rt-rate up', '−50% travel'))
      extra.appendChild(row)
      any = true
    }
    const owner = ownerAt(st, q, r)
    if (owner && owner !== 'wild') {
      const f = st.factions.get(owner)
      const row = el('div', 'rt-line')
      row.appendChild(el('span', 'rt-label', 'Territory'))
      const rr = el('span', 'rt-rate', owner === st.me ? 'yours' : f ? f.name : 'claimed')
      if (f) rr.style.color = f.color
      row.appendChild(rr)
      extra.appendChild(row)
      any = true
    }
    if (any) tilePanel.appendChild(extra)
  }

  if (gameCanvas) {
    // The 3D view resolves the hovered hex itself by raycasting the actual terrain
    // mesh (elevation-correct, cliff walls included) and writes it to st.hover; its
    // 2.5D screen→world math doesn't apply. The 2.5D view has no such pick, so it
    // reprojects the cursor with hexAtWorld (not the flat pixelToHex) to read the
    // tile whose lifted top is under the cursor rather than the flat tile beneath it.
    const use3d = use3dView()
    window.addEventListener('mousemove', (e) => {
      // Keep showing the last tile while the cursor is over the UI.
      if (e.target !== gameCanvas) return
      let q: number, r: number
      if (use3d) {
        if (!st.hover) return
        q = st.hover.q; r = st.hover.r
      } else {
        const rect = gameCanvas.getBoundingClientRect()
        const wpt = screenToWorld(st, gameCanvas, e.clientX - rect.left, e.clientY - rect.top)
        const h = hexAtWorld(st, wpt.x, wpt.y)
        q = h.q; r = h.r
      }
      const key = q + ',' + r
      if (key !== lastTileKey) {
        lastTileKey = key
        fillTilePanel(q, r)
      }
      tilePanel.classList.remove('hidden')
    })
  }

  const tbRight = el('div', 'tb-right')
  topbar.appendChild(tbRight)
  const dayChip = el('div', 'chip day')
  const dayIcon = el('span', 'chip-icon', '☀️')
  dayChip.appendChild(dayIcon)
  const dayClock = el('span', 'chip-amt', '06:00')
  dayChip.appendChild(dayClock)
  tbRight.appendChild(dayChip)
  const onlineChip = el('div', 'chip online-chip')
  onlineChip.appendChild(el('span', 'chip-icon led', '●'))
  const onlineAmt = el('span', 'chip-amt', '0')
  onlineChip.appendChild(onlineAmt)
  onlineChip.title = 'Players online'
  tbRight.appendChild(onlineChip)

  const diploBtn = el('button', 'btn btn-top', '🤝')
  diploBtn.title = 'Diplomacy (P)'
  diploBtn.setAttribute('aria-label', 'Diplomacy')
  const diploBadge = el('span', 'top-badge hidden', '')
  diploBtn.appendChild(diploBadge)
  tbRight.appendChild(diploBtn)
  const discordBtn = el('a', 'btn btn-top discord-top') as HTMLAnchorElement
  discordBtn.innerHTML = DISCORD_SVG
  discordBtn.href = 'https://discord.gg/QqEkAvCYdb'
  discordBtn.target = '_blank'
  discordBtn.rel = 'noopener'
  discordBtn.title = 'Join the Serf Land Discord community'
  discordBtn.setAttribute('aria-label', 'Join the Serf Land Discord community (opens a new tab)')
  tbRight.appendChild(discordBtn)
  const lbBtn = el('button', 'btn btn-top lb-btn', '🏆')
  lbBtn.title = 'Leaderboard — the mightiest kingdoms'
  lbBtn.setAttribute('aria-label', 'Leaderboard')
  tbRight.appendChild(lbBtn)
  const helpBtn = el('button', 'btn btn-top', '❓')
  helpBtn.title = 'How to play (H)'
  helpBtn.setAttribute('aria-label', 'How to play')
  tbRight.appendChild(helpBtn)

  // ---- Leaderboard (🏆): top kingdoms by hexes and serfs -----------------------
  // A compact floating panel under the chronicle; open/closed persists.

  const lbPanel = el('div', 'panel lb-panel hidden')
  lbPanel.appendChild(el('div', 'lb-title', '🏆 Mightiest kingdoms'))
  const lbClose = el('button', 'mobile-lb-close', '✕') as HTMLButtonElement
  lbClose.type = 'button'
  lbClose.setAttribute('aria-label', 'Close leaderboard')
  lbClose.title = 'Close leaderboard'
  lbClose.onclick = () => setLbOpen(false)
  lbPanel.appendChild(lbClose)
  const lbBody = el('div', 'lb-body')
  lbPanel.appendChild(lbBody)
  root.appendChild(lbPanel)
  let lbOpen = false
  // A saved desktop panel must not cover the opening view on a phone. The
  // Settings menu still lets the player open it deliberately on either layout.
  if (!window.matchMedia(MOBILE_HUD_QUERY).matches) {
    try { lbOpen = localStorage.getItem('hexLbOpen') === '1' } catch { /* private mode */ }
  }

  function renderLeaderboard(): void {
    if (!lbOpen) return
    lbBody.replaceChildren()
    const lb = st.leaderboard
    if (!lb || (lb.hexes.length === 0 && lb.serfs.length === 0)) {
      lbBody.appendChild(el('div', 'lb-empty', 'No kingdoms stand yet.'))
      return
    }
    const section = (cap: string, rows: { id: string; name: string; color: string; n: number }[]): void => {
      lbBody.appendChild(el('div', 'lb-cap', cap))
      for (let i = 0; i < rows.length; i++) {
        const rw = rows[i]
        const row = el('div', 'lb-row' + (rw.id === st.me ? ' me' : ''))
        row.appendChild(el('span', 'lb-rank', String(i + 1)))
        const sw = el('span', 'swatch')
        sw.style.background = rw.color
        row.appendChild(sw)
        row.appendChild(el('span', 'lb-name', rw.name))
        row.appendChild(el('span', 'lb-n', String(rw.n)))
        lbBody.appendChild(row)
      }
      if (rows.length === 0) lbBody.appendChild(el('div', 'lb-empty', '—'))
    }
    section('⬡ Most hexes', lb.hexes)
    section('👥 Most serfs', lb.serfs)
  }

  function setLbOpen(open: boolean): void {
    lbOpen = open
    try { localStorage.setItem('hexLbOpen', open ? '1' : '0') } catch { /* private mode */ }
    lbPanel.classList.toggle('hidden', !open)
    lbBtn.classList.toggle('active', open)
    renderLeaderboard()
  }
  lbBtn.onclick = () => setLbOpen(!lbOpen)
  st.on('leaderboard', renderLeaderboard)
  if (lbOpen) setLbOpen(true)

  // Settings gear + dropdown (Log out). The gear stays hidden until the boot
  // sequence confirms a session and calls setAccount(); there's nothing to
  // configure or log out of on the sign-in gate.
  const settingsWrap = el('div', 'settings-wrap hidden')
  const settingsBtn = el('button', 'btn btn-top', '⚙️') as HTMLButtonElement
  settingsBtn.title = 'Settings'
  settingsBtn.setAttribute('aria-label', 'Settings')
  settingsBtn.setAttribute('aria-expanded', 'false')
  settingsBtn.setAttribute('aria-controls', 'settings-menu')
  settingsWrap.appendChild(settingsBtn)
  tbRight.appendChild(settingsWrap)

  // The dropdown lives on the root (a fixed-position overlay), not inside the top
  // bar — the top bar forms its own stacking context, so a menu nested in it would
  // sit *below* toasts/panels. Positioned under the gear each time it opens.
  const settingsMenu = el('div', 'settings-menu hidden')
  settingsMenu.id = 'settings-menu'
  const settingsHead = el('div', 'settings-head', 'Signed in')
  const logoutBtn = el('button', 'settings-item settings-logout') as HTMLButtonElement
  logoutBtn.innerHTML = '<span class="si-ico">⏻</span><span>Log out</span>'
  // Full-page nav to /auth/logout clears the session cookie and 302s to / — the
  // reload lands on the sign-in gate (net socket and state are torn down with it).
  logoutBtn.onclick = () => {
    location.href = '/auth/logout'
  }
  // Start over: abandon the current realm and found a new village. Destructive,
  // so a second explicit confirm is required inside the menu.
  const restartBtn = el('button', 'settings-item settings-restart') as HTMLButtonElement
  restartBtn.innerHTML = '<span class="si-ico">⚑</span><span>Found a new village</span>'
  const confirmBox = el('div', 'settings-confirm hidden')
  const confirmText = el('div', 'settings-confirm-text', '')
  const confirmYes = el('button', 'btn settings-danger', 'Abandon & restart') as HTMLButtonElement
  const confirmNo = el('button', 'btn settings-cancel', 'Cancel') as HTMLButtonElement
  const confirmRow = el('div', 'settings-confirm-row')
  confirmRow.appendChild(confirmYes)
  confirmRow.appendChild(confirmNo)
  confirmBox.appendChild(confirmText)
  confirmBox.appendChild(confirmRow)

  // 2.5D height-view toggle. Only meaningful on elevation worlds, so it's
  // hidden until the client has seen elevation data (revealed in toggleSettings).
  // This is the phone-friendly equivalent of the desktop `L` shortcut.
  // Sound toggle (synth cues for builds, raids, births…).
  const soundBtn = el('button', 'settings-item') as HTMLButtonElement
  const renderSoundBtn = (): void => {
    soundBtn.innerHTML = soundsMuted()
      ? '<span class="si-ico">🔇</span><span>Sounds: off</span>'
      : '<span class="si-ico">🔊</span><span>Sounds: on</span>'
  }
  renderSoundBtn()
  soundBtn.onclick = () => {
    setMuted(!soundsMuted())
    renderSoundBtn()
  }

  const discordItem = el('a', 'settings-item settings-discord') as HTMLAnchorElement
  discordItem.innerHTML = '<span class="si-ico">' + DISCORD_SVG + '</span><span>Discord community ↗</span>'
  discordItem.href = 'https://discord.gg/QqEkAvCYdb'
  discordItem.target = '_blank'
  discordItem.rel = 'noopener'
  discordItem.setAttribute('aria-label', 'Join the Discord community (opens a new tab)')
  const clogBtn = el('button', 'settings-item') as HTMLButtonElement
  clogBtn.innerHTML = '<span class="si-ico">📜</span><span>What’s new</span>'
  clogBtn.onclick = () => changelog.toggle()

  // Phones can't fit the whole top-right button row alongside the resource
  // readout, so the secondary actions live here in the menu too. These items
  // are hidden on desktop (where the buttons themselves show) via CSS and just
  // delegate to the real buttons' handlers.
  const menuAction = (icon: string, label: string, src: HTMLElement): HTMLButtonElement => {
    const it = el('button', 'settings-item mobile-only-item') as HTMLButtonElement
    it.innerHTML = `<span class="si-ico">${icon}</span><span>${label}</span>`
    it.onclick = () => {
      toggleSettings(false)
      src.click()
    }
    return it
  }
  const diploItem = menuAction('🤝', 'Diplomacy', diploBtn)
  const lbItem = menuAction('🏆', 'Leaderboard', lbBtn)
  const helpItem = menuAction('❓', 'How to play', helpBtn)

  settingsMenu.appendChild(settingsHead)
  settingsMenu.appendChild(diploItem)
  settingsMenu.appendChild(lbItem)
  settingsMenu.appendChild(helpItem)
  settingsMenu.appendChild(discordItem)
  settingsMenu.appendChild(clogBtn)
  settingsMenu.appendChild(soundBtn)
  settingsMenu.appendChild(logoutBtn)
  settingsMenu.appendChild(restartBtn)
  settingsMenu.appendChild(confirmBox)
  root.appendChild(settingsMenu)

  function setRestartConfirm(on: boolean): void {
    if (on) {
      const nm = (st.me ? st.factions.get(st.me)?.name : '') || 'your realm'
      confirmText.textContent = `Abandon ${nm} and found a new village? Your old realm is left to crumble.`
    }
    confirmBox.classList.toggle('hidden', !on)
    settingsHead.classList.toggle('hidden', on)
    logoutBtn.classList.toggle('hidden', on)
    restartBtn.classList.toggle('hidden', on)
  }
  restartBtn.onclick = () => setRestartConfirm(true)
  confirmNo.onclick = () => setRestartConfirm(false)
  confirmYes.onclick = () => {
    tx({ t: 'restart' })
    setRestartConfirm(false)
    toggleSettings(false)
  }

  let settingsOpen = false
  function toggleSettings(open?: boolean): void {
    settingsOpen = open ?? !settingsOpen
    if (settingsOpen) {
      setRestartConfirm(false) // always open in the default view // elevation worlds only
      const r = settingsBtn.getBoundingClientRect()
      settingsMenu.style.top = Math.round(r.bottom + 6) + 'px'
      settingsMenu.style.right = Math.round(window.innerWidth - r.right) + 'px'
    }
    settingsMenu.classList.toggle('hidden', !settingsOpen)
    settingsBtn.classList.toggle('active', settingsOpen)
    settingsBtn.setAttribute('aria-expanded', String(settingsOpen))
  }
  settingsBtn.onclick = (e) => {
    e.stopPropagation()
    toggleSettings()
  }
  // Dismiss on outside click or Escape.
  document.addEventListener('click', (e) => {
    const t = e.target as Node
    if (settingsOpen && !settingsMenu.contains(t) && !settingsBtn.contains(t)) toggleSettings(false)
  })
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && settingsOpen) toggleSettings(false)
  })

  /** Reveal the Settings gear once signed in, labelling it with the provider. */
  function setAccount(provider?: string): void {
    settingsHead.textContent =
      provider === 'google'
        ? 'Signed in · Google'
        : provider === 'discord'
          ? 'Signed in · Discord'
          : provider === 'steam'
            ? 'Signed in · Steam'
            : 'Signed in'
    settingsWrap.classList.remove('hidden')
  }

  const connBar = el('div', 'connbar hidden', '⚠ Connecting to the realm…')
  root.appendChild(connBar)
  let everConnected = false

  // Full-screen gate: grays the game out and blocks play until the player acts.
  // Used both for the "new version — refresh" prompt (server-pushed) and the
  // "you're playing in another window" takeover notice.
  const gateBack = el('div', 'reload-gate hidden')
  const gatePanel = el('div', 'reload-panel')
  gatePanel.appendChild(el('div', 'join-rune', '⬡'))
  const gateTitle = el('h1', 'reload-title', '')
  gatePanel.appendChild(gateTitle)
  const gateSub = el('div', 'reload-sub', '')
  gatePanel.appendChild(gateSub)
  const gateBtn = el('button', 'btn reload-btn', '') as HTMLButtonElement
  gatePanel.appendChild(gateBtn)
  const gateCount = el('div', 'reload-count', '')
  gatePanel.appendChild(gateCount)
  gateBack.appendChild(gatePanel)
  root.appendChild(gateBack)

  let gateArmed = false
  function showGate(o: {
    title: string
    sub: string
    btn: string
    onClick: () => void
    autoReloadSec?: number
  }): void {
    if (gateArmed) return
    gateArmed = true
    gateTitle.textContent = o.title
    gateSub.textContent = o.sub
    gateBtn.textContent = o.btn
    gateBtn.onclick = o.onClick
    gateCount.textContent = ''
    gateBack.classList.remove('hidden')
    if (o.autoReloadSec) {
      let left = o.autoReloadSec
      const step = (): void => {
        gateCount.textContent = left > 0 ? `Reloading automatically in ${left}s…` : 'Reloading…'
        if (left <= 0) return location.reload()
        left--
        window.setTimeout(step, 1000)
      }
      step()
    }
  }
  st.on('reload', (reason?: string) =>
    showGate({
      title: 'A new version is ready',
      sub: reason || 'Refresh to keep playing.',
      btn: 'Refresh now',
      onClick: () => location.reload(),
      autoReloadSec: 12,
    }),
  )
  st.on('superseded', () =>
    showGate({
      title: 'Playing in another window',
      sub: 'Serf Land is open in another tab or on another device. You can only play in one place at a time.',
      btn: 'Play here',
      onClick: () => location.reload(),
    }),
  )

  // resource delta tracking (client-side, from self snapshots)
  const samples: { t: number; s: Stockpile }[] = []

  function renderTopbar(): void {
    const self = st.self
    if (self) {
      const first = samples[0]
      const last = samples[samples.length - 1]
      const dtMin = first && last ? (last.t - first.t) / 60000 : 0
      for (const res of RESOURCES) {
        const v = self.stockpile[res] ?? 0
        resAmount[res].textContent = String(Math.floor(v))
        resChip[res].setAttribute('aria-label', `${Math.floor(v)} ${res} stored. Show stores and production`)
        // Show the sustained economy rate (production − upkeep), which is exactly
        // the tooltip's Net, so the chip and the hover breakdown always agree.
        // One-off costs (building, training) show up as the stored amount dropping,
        // not as a persistent negative rate. Fall back to sampled change only if
        // the server hasn't sent flows yet.
        let rate: number | null = null
        const flow = self.flows?.[res]
        if (res === 'food' && self.food) rate = self.food.net
        else if (flow) rate = flow.net
        else if (dtMin > 0.05) rate = (last.s[res] - first.s[res]) / dtMin
        let deltaTxt = ''
        let deltaCls = 'chip-delta'
        if (rate !== null && Math.abs(rate) >= 0.5) {
          deltaTxt = (rate > 0 ? '+' : '−') + Math.abs(rate).toFixed(Math.abs(rate) >= 10 ? 0 : 1)
          deltaCls += rate > 0 ? ' up' : ' down'
        }
        resDelta[res].textContent = deltaTxt
        resDelta[res].className = deltaCls
      }
      // Storage is a TOTAL item cap across all goods: when the realm is nearly
      // full every resource is equally blocked, so all chips redden together.
      let totalStored = 0
      for (const res of RESOURCES) totalStored += self.stockpile[res] ?? 0
      const nearCap = totalStored >= self.capacity * 0.9
      for (const res of RESOURCES) resChip[res].classList.toggle('full', nearCap)
      popAmt.textContent = self.pop + '/' + self.popCap
      popChip.setAttribute('aria-label', `${self.pop} villagers, housing for ${self.popCap}. Open village roster`)
      popChip.classList.toggle('full', self.pop >= self.popCap)
      let idle = 0
      for (const u of st.units.values()) {
        if (u.owner === st.me && u.type === 'citizen' && !u.hauler && u.state === 'idle' && !u.scav && u.supply === undefined) idle++
      }
      idleAmt.textContent = String(idle)
      idleChip.setAttribute('aria-label', `Select ${idle} idle ${idle === 1 ? 'worker' : 'workers'}`)
      idleChip.classList.toggle('hidden', idle === 0)
      refreshHint()
      roster.refresh()
      assign.refresh()
      renderResTip() // keep the hover breakdown live as self updates
      renderPopTip()
    }
    const phase = ((st.tick % DAY_TICKS) + DAY_TICKS) % DAY_TICKS / DAY_TICKS
    const isDay = phase < 0.5
    dayIcon.textContent = isDay ? '☀️' : '🌙'
    // Map the day phase to a 24h clock with dawn at phase 0 (06:00), so daylight
    // (phase < 0.5) runs 06:00–18:00 and night 18:00–06:00.
    const hoursF = (phase * 24 + 6) % 24
    const hh = Math.floor(hoursF)
    const mm = Math.floor((hoursF - hh) * 60)
    const clock = String(hh).padStart(2, '0') + ':' + String(mm).padStart(2, '0')
    dayClock.textContent = 'Day ' + (Math.floor(st.tick / DAY_TICKS) + 1) + ' · ' + clock
    dayChip.title =
      'Day ' + (Math.floor(st.tick / DAY_TICKS) + 1) + ' · ' + clock +
      ' — ' + (isDay ? 'daylight' : 'nightfall')
  }

  function renderOnline(): void {
    let n = 0
    for (const f of st.factions.values()) if (f.kind === 'player' && f.controller !== 'neutral' && f.online) n++
    onlineAmt.textContent = String(n)
    onlineChip.classList.toggle('some', n > 0)
  }

  // ---- Toasts ---------------------------------------------------------------

  const toasts = el('div', 'toasts')
  root.appendChild(toasts)

  function toast(kind: string, msg: string, at?: { q: number; r: number }): void {
    const t = el('div', 'toast k-' + kind)
    t.appendChild(el('div', 'toast-msg', msg))
    if (at) {
      t.appendChild(el('span', 'toast-loc', '⌖ ' + at.q + ',' + at.r))
      t.classList.add('clickable')
      t.title = 'Click to view'
      t.onclick = () => jumpTo(at.q, at.r)
    }
    toasts.prepend(t)
    while (toasts.children.length > 6) toasts.lastElementChild!.remove()
    requestAnimationFrame(() => t.classList.add('show'))
    window.setTimeout(() => {
      t.classList.add('gone')
      window.setTimeout(() => t.remove(), 450)
    }, 7000)
  }

  // ---- Panels ----------------------------------------------------------------

  const chron = initChronicle(ctx)
  const selPanel = initSelectionPanel(ctx)
  // Stack the unit-info panel beneath the tile-info panel in the bottom-left.
  const selEl = root.querySelector('.sel-panel')
  if (selEl) blStack.appendChild(selEl)
  const diplo = initDiplomacy(ctx, (n) => {
    diploBadge.textContent = String(n)
    diploBadge.classList.toggle('hidden', n === 0)
    diploBtn.setAttribute('aria-label', n ? `Diplomacy · ${n} alerts` : 'Diplomacy')
  })
  const help = initHelpOverlay(ctx)
  const defeat = initDefeatOverlay(ctx)
  diploBtn.onclick = () => diplo.toggle()
  helpBtn.onclick = () => help.toggle()

  // ---- Build bar ----------------------------------------------------------------

  const buildbar = el('div', 'panel buildbar')
  buildbar.setAttribute('aria-label', 'Build and manage your village')
  const dockHead = el('div', 'dock-head')
  dockHead.appendChild(el('span', 'dock-title', 'Build your settlement'))
  dockHead.appendChild(el('span', 'dock-hint', 'B · build    Esc · cancel'))
  buildbar.appendChild(dockHead)
  root.appendChild(buildbar)

  // The dock is grouped: pick a category tab, see its buildings. Number
  // hotkeys stay bound to the global BUILD_ORDER regardless of the open tab.
  const BUILD_GROUPS: { key: string; icon: string; label: string; types: BuildingType[] }[] = [
    { key: 'town', icon: '🏛', label: 'Town', types: ['townhall', 'house', 'storehouse', 'road'] },
    { key: 'gather', icon: '🌾', label: 'Gathering', types: ['farm', 'chop', 'plant', 'quarry', 'mine', 'pier'] },
    { key: 'craft', icon: '⚒', label: 'Crafting', types: ['workbench', 'barracks'] },
    { key: 'defense', icon: '🗼', label: 'Defense', types: ['tower', 'palisade', 'dummy'] },
    // Land-shaping — only meaningful (and only shown) on worlds with heights.
    { key: 'terra', icon: '⛰', label: 'Terraform', types: ['ramp', 'dig', 'fill', 'fillsand'] },
  ]
  const previews = createBuildingPreviews(BUILD_GROUPS.flatMap(g => g.types))
  let openGroup = 'town'
  try { openGroup = localStorage.getItem('hexBuildTab') || 'town' } catch { /* private mode */ }
  if (!BUILD_GROUPS.some((g) => g.key === openGroup)) openGroup = 'town'

  const tabRow = el('div', 'bld-tabs')
  tabRow.id = 'build-categories'
  buildbar.appendChild(tabRow)
  const btnRow = el('div', 'bld-row')
  btnRow.id = 'build-options'
  buildbar.appendChild(btnRow)
  const buildBtns = new Map<BuildingType, HTMLButtonElement>()
  const tabBtns = new Map<string, HTMLButtonElement>()

  // Chop Trees sub-mode switch (shown only while the Chop tool is armed): drag a box to
  // QUEUE trees for felling, or switch to REMOVE and drag to pull them off the queue.
  const chopModeRow = el('div', 'chop-mode hidden')
  const chopAddBtn = el('button', 'chop-mode-btn', '🪓 Chop') as HTMLButtonElement
  const chopDelBtn = el('button', 'chop-mode-btn', '✕ Remove') as HTMLButtonElement
  chopAddBtn.title = 'Drag a box over trees to queue them for felling'
  chopDelBtn.title = 'Drag a box over queued trees to remove them from the chop queue'
  chopAddBtn.onclick = () => { st.chopCancel = false; refreshBuildBar() }
  chopDelBtn.onclick = () => { st.chopCancel = true; refreshBuildBar() }
  chopModeRow.appendChild(chopAddBtn)
  chopModeRow.appendChild(chopDelBtn)
  buildbar.appendChild(chopModeRow)
  const plantingHint = el('div', 'forestry-build-hint hidden', 'Click or drag over empty grass · 1 food per hex · grows in 3 game days')
  buildbar.appendChild(plantingHint)

  for (const g of BUILD_GROUPS) {
    const tab = el('button', 'bld-tab', `${g.icon} ${g.label}`) as HTMLButtonElement
    tab.dataset.buildGroup = g.key
    tab.setAttribute('aria-label', g.label + ' buildings')
    tab.onclick = () => {
      st.emit('ui:build:reveal')
      openGroup = g.key
      try { localStorage.setItem('hexBuildTab', g.key) } catch { /* private mode */ }
      renderGroup()
    }
    tabRow.appendChild(tab)
    tabBtns.set(g.key, tab)
  }

  function renderGroup(): void {
    // The Terraform tab exists only on height worlds; fall back if it vanishes.
    const terraTab = tabBtns.get('terra')
    if (terraTab) terraTab.style.display = st.hasElev ? '' : 'none'
    if (openGroup === 'terra' && !st.hasElev) openGroup = 'town'
    for (const [k, t] of tabBtns) {
      t.classList.toggle('open', k === openGroup)
      t.setAttribute('aria-pressed', String(k === openGroup))
    }
    btnRow.replaceChildren()
    buildBtns.clear()
    const g = BUILD_GROUPS.find((x) => x.key === openGroup)!
    const types = g.types
    for (const type of types) {
      const def = BUILDINGS[type]
      const i = BUILD_ORDER.indexOf(type)
      const btn = el('button', 'bld-btn') as HTMLButtonElement
      btn.dataset.buildType = type
      btn.setAttribute('aria-label', def.name)
      if (i >= 0 && i < 10) btn.appendChild(el('span', 'bld-key', String((i + 1) % 10)))
      const preview = previews.get(type)
      if (preview) {
        const img = el('img', 'bld-preview') as HTMLImageElement
        img.src = preview; img.alt = ''; img.draggable = false
        btn.appendChild(img)
      } else btn.appendChild(el('span', 'bld-icon', BUILD_ICON[type]))
      btn.appendChild(el('span', 'bld-name', def.name))
      btn.appendChild(el('span', 'bld-cost', fmtCost(def.cost) + (type === 'plant' ? ' / hex' : '')))
      btn.appendChild(buildTooltip(type))
      btn.onclick = () => toggleBuild(type)
      btnRow.appendChild(btn)
      buildBtns.set(type, btn)
    }
    refreshBuildBar()
  }

  // Warns the player, while a replacement town hall is in build mode, that placing it
  // will retire their current one once it finishes.
  const relocateWarn = el(
    'div',
    'relocate-warn hidden',
    '⚠ Your current town hall will be dismantled once this one is built — its stores spill and haulers carry them to the new hall.',
  )
  root.appendChild(relocateWarn)

  function hasFinishedTownhall(): boolean {
    for (const b of st.buildings.values()) {
      if (b.owner === st.me && b.type === 'townhall' && b.done) return true
    }
    return false
  }

  function toggleBuild(type: BuildingType): void {
    if (st.observer) return // spectators don't build
    st.buildMode = st.buildMode === type ? null : type
    st.pierRotate = 0 // fresh placement starts at the default orientation
    st.chopCancel = false // arming (or re-arming) the Chop tool always starts in queue mode
    // Selecting from a hotkey may target a building in another tab — open it.
    if (st.buildMode) {
      st.emit('ui:build:reveal')
      const g = BUILD_GROUPS.find((x) => x.types.includes(type))
      if (g && g.key !== openGroup) {
        openGroup = g.key
        renderGroup()
        return
      }
    }
    refreshBuildBar()
  }

  function refreshBuildBar(): void {
    const terraTab = tabBtns.get('terra')
    if (terraTab) {
      const want = st.hasElev ? '' : 'none'
      if (terraTab.style.display !== want) terraTab.style.display = want
    }
    for (const [type, btn] of buildBtns) {
      // Affordability never blocks placement: a site is a free blueprint that carriers
      // fill from storage as the goods arrive. We only tint the cost red (.poor) to flag
      // "not stocked yet" — the blueprint can still be planted and will sit unbuilt until
      // you gather the materials.
      const affordable = canAfford(st, BUILDINGS[type].cost)
      btn.classList.toggle('poor', !affordable)
      btn.classList.toggle('active', st.buildMode === type)
    }
    relocateWarn.classList.toggle('hidden', st.buildMode !== 'townhall' || !hasFinishedTownhall())
    // Box-order sub-mode switch: visible while a box order (chop / terraform) is armed;
    // the queue/remove state is meaningless otherwise, so keep it cleared then.
    const boxing = !!st.buildMode && isBoxOrder(st.buildMode)
    chopModeRow.classList.toggle('hidden', !boxing)
    plantingHint.classList.toggle('hidden', st.buildMode !== 'plant')
    plantingHint.textContent = st.chopCancel
      ? 'Click or drag over your planting orders to cancel or uproot them'
      : 'Click or drag over empty grass · 1 food per hex · grows in 3 game days'
    if (!boxing) st.chopCancel = false
    if (boxing) {
      chopAddBtn.textContent = `${BUILD_ICON[st.buildMode!]} ${BUILDINGS[st.buildMode!].name}`
      chopAddBtn.title = st.buildMode === 'plant'
        ? 'Click or drag over empty grassland to plant trees'
        : `Click or drag to queue ${BUILDINGS[st.buildMode!].name.toLowerCase()}`
      chopDelBtn.title = st.buildMode === 'plant'
        ? 'Remove planting orders or uproot growing saplings'
        : 'Click or drag over your orders to remove them'
    }
    chopAddBtn.classList.toggle('active', boxing && !st.chopCancel)
    chopDelBtn.classList.toggle('active', boxing && st.chopCancel)
  }
  renderGroup() // initial dock

  st.on('ui:build', () => {
    buildbar.classList.toggle('emphasized')
  })
  st.on('ui:diplo', () => diplo.toggle())
  st.on('ui:help', () => help.toggle())
  st.on('ui:roster', () => roster.toggle())

  // ---- Chat -----------------------------------------------------------------------

  const chatWrap = el('div', 'chat')
  const chatLines = el('div', 'chat-lines')
  const chatInputRow = el('div', 'chat-input-row')
  const chatPrompt = el('span', 'chat-prompt', '✎')
  const chatInput = el('input', 'chat-input') as HTMLInputElement
  chatInput.type = 'text'
  chatInput.maxLength = CHAT_MAX
  chatInput.placeholder = 'Press Enter to speak…'
  chatInput.autocomplete = 'off'
  chatInput.spellcheck = false
  chatInputRow.appendChild(chatPrompt)
  chatInputRow.appendChild(chatInput)
  chatWrap.appendChild(chatLines)
  chatWrap.appendChild(chatInputRow)
  root.appendChild(chatWrap)

  const chatSeenAt = new WeakMap<object, number>()

  function renderChat(): void {
    const now = Date.now()
    const focused = document.activeElement === chatInput
    const entries = st.chat.slice(-6)
    chatLines.replaceChildren()
    const visible = entries.filter((c: object) => {
      if (!chatSeenAt.has(c)) chatSeenAt.set(c, now)
      return focused || now - (chatSeenAt.get(c) ?? now) < 45000
    })
    visible.forEach((c: { from: string; name: string; text: string }, i: number) => {
      const line = el('div', 'chat-line')
      line.style.opacity = String(0.55 + (0.45 * (i + 1)) / visible.length)
      const nm = el('span', 'chat-name', c.name)
      nm.style.color = colorOf(st, c.from)
      line.appendChild(nm)
      line.appendChild(el('span', 'chat-text', c.text))
      chatLines.appendChild(line)
    })
  }

  chatInput.addEventListener('keydown', (e) => {
    e.stopPropagation() // don't let WASD/hotkey handlers see typing
    if (e.key === 'Enter') {
      const text = chatInput.value.trim().slice(0, CHAT_MAX)
      if (text) tx({ t: 'chat', text })
      chatInput.value = ''
      chatInput.blur()
      e.preventDefault()
    } else if (e.key === 'Escape') {
      chatInput.value = ''
      chatInput.blur()
      e.preventDefault()
    }
  })
  chatInput.addEventListener('focus', () => {
    chatWrap.classList.add('typing')
    renderChat()
  })
  chatInput.addEventListener('blur', () => {
    chatWrap.classList.remove('typing')
    renderChat()
  })

  // ---- Minimap frame -----------------------------------------------------------------

  const mmWrap = el('div', 'panel minimap-wrap')
  const mmHead = el('div', 'mm-head', '🗺 The Realm')
  const mmCanvas = el('canvas', 'minimap') as HTMLCanvasElement
  mmCanvas.id = 'minimap'
  mmCanvas.width = 208
  mmCanvas.height = 208
  mmWrap.appendChild(mmHead)
  mmWrap.appendChild(mmCanvas)
  root.appendChild(mmWrap)
  initMobileUI(st, root, topbar, buildbar, mmWrap)

  // ---- Join modal --------------------------------------------------------------------

  const joinBack = el('div', 'modal-back join hidden')
  joinBack.tabIndex = -1
  joinBack.setAttribute('role', 'dialog')
  joinBack.setAttribute('aria-modal', 'true')
  joinBack.setAttribute('aria-labelledby', 'landing-title')
  const landing = el('div', 'landing')
  const landMap = el('canvas', 'landing-map') as HTMLCanvasElement
  landing.appendChild(landMap)
  landing.appendChild(el('div', 'landing-scrim'))
  const landCard = el('div', 'landing-card')
  landing.appendChild(landCard)
  joinBack.appendChild(landing)
  root.appendChild(joinBack)

  const GOOGLE_SVG =
    '<svg viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>'
  const STEAM_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z"/></svg>'

  // Left column: the pitch — brand, thesis headline, and craft chips — over the shadow.
  const pitch = el('div', 'landing-pitch')
  const brand = el('div', 'landing-brand')
  brand.appendChild(el('span', 'landing-crest', '⬡'))
  brand.appendChild(el('span', 'landing-wordmark', 'Serf Land'))
  pitch.appendChild(brand)
  const landingTitle = el('h1', 'landing-headline', 'Raise a realm that never sleeps.')
  landingTitle.id = 'landing-title'
  pitch.appendChild(landingTitle)
  pitch.appendChild(
    el(
      'p',
      'landing-sub',
      'A living, persistent world of hexes. Settle a village, work the land, forge armies and carve a kingdom from the wilds — the map keeps turning while you’re away.',
    ),
  )
  const feats = el('div', 'landing-features')
  for (const [ic, title, desc] of [
    ['🌾', 'Work the land', 'Farm, log, mine and haul to feed a growing people.'],
    ['🏰', 'Build your seat', 'A town hall, houses, roads and walls — hex by hex.'],
    ['⚔️', 'Raise armies', 'Arm your serfs and march on rival realms.'],
    ['🤝', 'Ally or raid', 'Forge alliances or fight for new territory.'],
  ]) {
    const it = el('div', 'feature')
    const icon = el('span', 'feature-icon')
    icon.appendChild(el('span', 'feature-glyph', ic))
    it.appendChild(icon)
    const ft = el('div', 'feature-text')
    ft.appendChild(el('span', 'feature-title', title))
    ft.appendChild(el('span', 'feature-desc', desc))
    it.appendChild(ft)
    feats.appendChild(it)
  }
  pitch.appendChild(feats)
  landCard.appendChild(pitch)

  // Right column: the sign-in card, floating over the lit side of the realm.
  const joinCol = el('div', 'landing-join')
  joinCol.appendChild(el('div', 'landing-join-title', 'Claim your village'))
  const signRow = el('div', 'join-signin')
  const googleBtn = el('button', 'btn signin-btn signin-google') as HTMLButtonElement
  googleBtn.type = 'button'
  googleBtn.innerHTML = GOOGLE_SVG + '<span>Sign in with Google</span>'
  googleBtn.onclick = () => {
    location.href = '/auth/google'
  }
  const steamBtn = el('button', 'btn signin-btn signin-steam') as HTMLButtonElement
  steamBtn.type = 'button'
  steamBtn.innerHTML = STEAM_SVG + '<span>Sign in with Steam</span>'
  steamBtn.onclick = () => {
    location.href = '/auth/steam'
  }
  const discordSignInBtn = el('button', 'btn signin-btn signin-discord') as HTMLButtonElement
  discordSignInBtn.type = 'button'
  discordSignInBtn.innerHTML = DISCORD_SVG + '<span>Sign in with Discord</span>'
  discordSignInBtn.onclick = () => {
    location.href = '/auth/discord'
  }
  signRow.appendChild(googleBtn)
  signRow.appendChild(discordSignInBtn)
  signRow.appendChild(steamBtn)
  joinCol.appendChild(signRow)
  const trust = el('div', 'landing-trust')
  for (const t of ['Free to play', 'No download', 'Runs in your browser']) {
    const item = el('div', 'trust-item')
    item.appendChild(el('span', 'trust-glyph', '⬡'))
    item.appendChild(el('span', 'trust-text', t))
    trust.appendChild(item)
  }
  joinCol.appendChild(trust)
  const discordLink = el('a', 'btn landing-discord') as HTMLAnchorElement
  discordLink.innerHTML = DISCORD_SVG + '<span>Join our Discord</span><span class="community-external" aria-hidden="true">↗</span>'
  discordLink.setAttribute('aria-label', 'Join the Discord community (opens a new tab)')
  discordLink.href = 'https://discord.gg/QqEkAvCYdb'
  discordLink.target = '_blank'
  discordLink.rel = 'noopener'
  joinCol.appendChild(discordLink)
  joinCol.appendChild(
    el(
      'div',
      'landing-alpha',
      '⚠ Super early alpha — actively being developed. The map can be wiped at any time.',
    ),
  )
  landCard.appendChild(joinCol)

  const beforeLoginInert = new Map<HTMLElement, boolean>()
  // The game HUD exists behind the landing page. Keep its controls out of the
  // keyboard and accessibility tree until the player has actually signed in.
  window.addEventListener('keydown', e => {
    if (joinBack.classList.contains('hidden')) return
    e.stopImmediatePropagation()
    if (e.key !== 'Tab') return
    if (e.shiftKey && (document.activeElement === googleBtn || document.activeElement === joinBack)) {
      e.preventDefault(); discordLink.focus()
    } else if (!e.shiftKey && document.activeElement === discordLink) {
      e.preventDefault(); googleBtn.focus()
    }
  }, true)

  /** Reveal the sign-in landing (shown when the player has no active session). */
  function showLogin(): void {
    for (const child of root.children) {
      if (!(child instanceof HTMLElement) || child === joinBack) continue
      if (!beforeLoginInert.has(child)) beforeLoginInert.set(child, child.inert)
      child.inert = true
    }
    joinBack.classList.remove('hidden')
    // Preserve the mobile introduction's scroll position; Tab brings the first
    // sign-in button into view without stepping through the hidden game HUD.
    joinBack.focus({ preventScroll: true })
    // Paint the realm backdrop now it has a measured size (it was display:none).
    requestAnimationFrame(() => paintLandingMap(landMap))
  }
  /** Hide the landing once a session is established (welcome received). */
  function hideLogin(): void {
    joinBack.classList.add('hidden')
    for (const [node, inert] of beforeLoginInert) node.inert = inert
    beforeLoginInert.clear()
  }
  st.on('welcome', hideLogin)
  st.on('welcome', () => {
    if (!st.observer) changelog.showIfUpdated()
  })
  st.on('welcome', () => {
    if (!st.observer) return
    // Spectator chrome: no building, no hints — just a badge.
    buildbar.classList.add('hidden')
    hintBar.classList.add('hidden')
    const badge = el('div', 'panel obs-badge', '👁 OBSERVER — fog disabled, read-only')
    root.appendChild(badge)
    // Realms list: click a player to jump to their town hall.
    const realms = el('div', 'panel obs-realms')
    realms.appendChild(el('div', 'obs-realms-title', 'Realms'))
    const list = el('div', 'obs-realms-list')
    realms.appendChild(list)
    root.appendChild(realms)
    const renderRealms = (): void => {
      list.replaceChildren()
      const fs = [...st.factions.values()]
        .filter((f2) => f2.kind === 'player')
        .sort((a, b2) => Number(b2.online ?? false) - Number(a.online ?? false) || (a.name < b2.name ? -1 : 1))
      for (const f2 of fs) {
        const row = el('button', 'obs-realm-row') as HTMLButtonElement
        const dot = el('span', 'dot ' + (f2.online ? 'on' : 'off'))
        const sw = el('span', 'swatch')
        sw.style.background = f2.color
        if (f2.controller !== 'neutral') row.appendChild(dot)
        row.appendChild(sw)
        row.appendChild(el('span', 'obs-realm-name', f2.name + (f2.controller === 'neutral' ? ' · Neutral AI' : '')))
        row.onclick = () => {
          if (f2.home) jumpTo(f2.home.q, f2.home.r)
        }
        list.appendChild(row)
      }
    }
    renderRealms()
    st.on('factions', renderRealms)
    st.on('faction', renderRealms)
  })
  window.addEventListener('resize', () => {
    if (!joinBack.classList.contains('hidden')) paintLandingMap(landMap)
  })

  // ---- Name-your-kingdom modal (first sign-in) -----------------------------------------
  // On the very first sign-in the account has no chosen kingdom name yet
  // (welcome.needsName), so we prompt for one before the player settles in.
  const nameBack = el('div', 'modal-back name-modal hidden')
  const namePanel = el('div', 'panel name-panel')
  nameBack.appendChild(namePanel)
  root.appendChild(nameBack)
  namePanel.appendChild(el('div', 'join-rune', '⬡'))
  namePanel.appendChild(el('h1', 'name-title', 'Name your kingdom'))
  namePanel.appendChild(
    el('div', 'name-sub', 'Choose the name your realm will be known by across the land.'),
  )
  const nameRow = el('div', 'name-row')
  const nameInput = el('input', 'name-input') as HTMLInputElement
  nameInput.type = 'text'
  nameInput.maxLength = NAME_MAX
  nameInput.placeholder = 'e.g. Highmarch, the Iron Vale…'
  nameInput.autocomplete = 'off'
  nameInput.spellcheck = false
  nameRow.appendChild(nameInput)
  const rerollBtn = el('button', 'name-reroll', '🎲') as HTMLButtonElement
  rerollBtn.type = 'button'
  rerollBtn.title = 'Suggest another name'
  rerollBtn.onclick = () => {
    nameInput.value = randomRealmName()
    nameInput.focus()
    nameInput.select()
  }
  nameRow.appendChild(rerollBtn)
  namePanel.appendChild(nameRow)
  const nameErr = el('div', 'name-err', '')
  namePanel.appendChild(nameErr)
  const nameBtn = el('button', 'btn name-btn', 'Found your kingdom') as HTMLButtonElement
  namePanel.appendChild(nameBtn)

  function submitName(): void {
    const v = nameInput.value.replace(/\s+/g, ' ').trim().slice(0, NAME_MAX)
    if (!v) {
      nameErr.textContent = 'Please enter a name.'
      nameInput.focus()
      return
    }
    tx({ t: 'name', name: v })
    nameBack.classList.add('hidden')
  }
  nameBtn.onclick = submitName
  nameInput.addEventListener('keydown', (e) => {
    e.stopPropagation() // keep WASD/hotkeys from firing while typing
    if (e.key === 'Enter') {
      submitName()
      e.preventDefault()
    }
  })

  function showNameModal(): void {
    // Suggest a random anonymous name — never the player's real sign-in name.
    // They can reroll or type their own before founding.
    nameInput.value = randomRealmName()
    nameErr.textContent = ''
    nameBack.classList.remove('hidden')
    nameInput.focus()
    nameInput.select()
  }
  st.on('welcome', (msg?: { needsName?: boolean }) => {
    if (msg?.needsName) showNameModal()
  })

  // ---- Events / chronicle wiring --------------------------------------------------------

  const seenEvents = new WeakSet<GameEvent>()
  let eventsBooted = false

  function processEvents(): void {
    for (const ev of st.events) {
      if (seenEvents.has(ev)) continue
      seenEvents.add(ev)
      if (ev.kind === 'decay') continue // floats over the building; no toast/chronicle spam
      chron.append(ev)
      if (eventsBooted) {
        toast(ev.kind, ev.msg, ev.q !== undefined && ev.r !== undefined ? { q: ev.q, r: ev.r } : undefined)
      }
    }
    eventsBooted = true
  }

  function diploToast(d: { from: string; to: string; state: string }): void {
    const me = st.me
    if (!me || !d || (d.from !== me && d.to !== me)) return
    const other = d.from === me ? d.to : d.from
    const otherName = st.factions.get(other)?.name ?? 'A stranger'
    if (d.state === 'proposed' && d.to === me) {
      toast('diplo', otherName + ' proposes an alliance — press P')
    } else if (d.state === 'allied') {
      toast('diplo', 'Alliance forged with ' + otherName + '!')
    } else if (d.state === 'broken') {
      toast('diplo', 'The alliance with ' + otherName + ' is broken.')
    } else if (d.state === 'declined' && d.to === me) {
      toast('diplo', otherName + ' declined your proposal.')
    }
  }

  // ---- GameState subscriptions ------------------------------------------------------------

  st.on('self', () => {
    const self = st.self
    if (self) {
      samples.push({ t: Date.now(), s: { ...self.stockpile } })
      const cutoff = Date.now() - 75000
      while (samples.length > 2 && samples[0].t < cutoff) samples.shift()
    }
    renderTopbar()
    refreshBuildBar()
    selPanel.refresh()
    defeat.refresh()
  })

  st.on('predict', () => {
    selPanel.refresh()
    assign.refresh()
  })
  st.on('tick', () => {
    renderTopbar()
    diplo.tick()
    if (st.selection.building !== null || st.selection.units.length) selPanel.refresh()
    refreshBuildBar() // keep build-mode highlight + relocation warning in sync after a place/cancel
    renderChat()
  })

  st.on('welcome', () => {
    renderTopbar()
    renderOnline()
    refreshBuildBar()
    defeat.refresh()
    diplo.refresh()
    processEvents()
  })

  st.on('factions', () => {
    renderOnline()
    diplo.refresh()
    renderChat()
  })

  st.on('events', processEvents)
  st.on('chat', renderChat)
  st.on('diplo', (d: { from: string; to: string; state: string }) => {
    if (d && d.state) {
      diplo.noteDiplo(d)
      diploToast(d)
    } else {
      diplo.refresh()
    }
  })
  st.on('error', (msg: unknown) => {
    if (msg === 'auth-required') {
      // The session no longer maps to a village here — surface the sign-in gate
      // instead of hanging forever on "Connecting to the realm…".
      showLogin()
      return
    }
    toast('error', typeof msg === 'string' ? msg : 'That cannot be done.')
  })

  // ---- Hotkeys (digits + chat focus; B/H/P arrive via st events from input.ts) -------------

  window.addEventListener('keydown', (e) => {
    const target = e.target as HTMLElement | null
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return
    if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return
    const modalUp =
      gateArmed ||
      !joinBack.classList.contains('hidden') ||
      !nameBack.classList.contains('hidden')
    if (e.key === 'Enter') {
      if (!modalUp && !st.self?.ruined) {
        chatInput.focus()
        e.preventDefault()
      }
      return
    }
    const idx = '1234567890'.indexOf(e.key)
    if (idx >= 0 && !modalUp) {
      toggleBuild(BUILD_ORDER[idx])
    }
  })

  // ---- Watcher: cheap poll for things with no event (selection, buildMode, socket) ---------

  let lastSig = ''
  let lastBuildMode = st.buildMode
  window.setInterval(() => {
    document.body.classList.toggle('build-mode', st.buildMode !== null)
    if (st.buildMode !== lastBuildMode) {
      lastBuildMode = st.buildMode
      if (st.buildMode) st.emit('ui:build:reveal')
    }

    if (st.connected) everConnected = true
    connBar.textContent = everConnected
      ? '⚠ Connection lost — reaching for the realm…'
      : '⏳ Connecting to the realm…'
    connBar.classList.toggle('hidden', st.connected)

    const sig =
      (st.buildMode ?? '') +
      '|' +
      (st.selection.building ?? '') +
      '|' +
      st.selection.units.join(',')
    if (sig !== lastSig) {
      lastSig = sig
      refreshBuildBar()
      selPanel.refresh()
      st.emit('ui:selection')
    }
  }, 150)

  renderTopbar()
  refreshBuildBar()

  return { showLogin, setAccount }
}
